import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { fmtDate as formatDate } from "@/lib/format";

export default function MoveDueDateDialog({ column, onOpenChange, onDone }) {
  const [preview, setPreview] = useState(null);
  const [date, setDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [reference, setReference] = useState("");

  useEffect(() => {
    if (!column) return;
    setPreview(null);
    setReference("");
    setDate(column.extended_to?.[0] || column.standard_due_date);
    api.get("/compliance/shift/preview", { params: { compliance_key: column.key, standard_due_date: column.standard_due_date } })
      .then((r) => setPreview(r.data)).catch((e) => toast.error(formatErr(e)));
  }, [column]);

  const submit = async (target) => {
    setBusy(true);
    try {
      const { data } = await api.post("/compliance/shift", { compliance_key: column.key, standard_due_date: column.standard_due_date, new_due_date: target, reference });
      toast.success(target === column.standard_due_date ? `Restored the standard date for ${data.updated} client(s)` : `Moved to ${formatDate(target)} for ${data.updated} client(s)`);
      onDone();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!column} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="move-due-dialog">
        <DialogHeader>
          <DialogTitle>Move due date — {column?.name}</DialogTitle>
          <DialogDescription>
            Use this when the government extends a deadline. Only this period changes; the next period goes back to the normal date automatically.
          </DialogDescription>
        </DialogHeader>
        {column && (
          <div className="space-y-4">
            <div className="text-sm">Standard due date: <b className="mono" data-testid="move-due-standard">{formatDate(column.standard_due_date)}</b></div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">New due date</Label>
              <Input data-testid="move-due-new-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 mono" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Notification / circular reference (optional)</Label>
              <Input data-testid="move-due-reference" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. CBIC Notification 12/2026" className="mt-1" />
            </div>
            <div className="text-xs text-slate-600" data-testid="move-due-preview">
              {preview ? <>Applies to <b>{preview.count}</b> open reminder(s): {preview.items.map((i) => i.entity).join(", ") || "—"}</> : "Loading…"}
            </div>
          </div>
        )}
        <DialogFooter className="gap-2">
          {column?.extended_to?.length > 0 && (
            <Button variant="outline" data-testid="move-due-restore" disabled={busy} onClick={() => submit(column.standard_due_date)}>Restore standard date</Button>
          )}
          <Button data-testid="move-due-submit" disabled={busy || !date || !preview?.count || date === column?.standard_due_date}
            onClick={() => submit(date)} className="bg-blue-700 hover:bg-blue-800 text-white">
            {busy ? "Moving…" : `Move for ${preview?.count ?? "…"} client(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
